import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebase';

/**
 * Upload file to Firebase Storage
 * Replaces the previous S3 upload functionality
 */
export async function uploadFileToS3(
  file: File, 
  options?: { makePublic?: boolean; keyPrefix?: string; onProgress?: (percent: number) => void }
): Promise<{ s3Key: string; publicUrl: string; bucket: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('Upload failed: User not authenticated');
      throw new Error('Not authenticated. Please log in to upload images.');
    }

    console.log('Uploading file:', { fileName: file.name, fileSize: file.size, fileType: file.type, userId: user.uid });

    // Generate unique filename
    const ext = file.name.split('.').pop() || 'bin';
    const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const folder = options?.keyPrefix || 'blog-images';
    const filePath = `${folder}/${safeName}`;

    console.log('File path:', filePath);

    // Create storage reference
    const storageRef = ref(storage, filePath);

    // Upload file (resumable with progress)
    console.log('Starting upload to Firebase Storage...');
    const uploadTask = uploadBytesResumable(storageRef, file);

    const result = await new Promise<{ publicUrl: string }>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (options?.onProgress) {
            options.onProgress(Math.round(progress));
          }
        },
        (error) => {
          reject(error);
        },
        async () => {
          try {
            const publicUrl = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({ publicUrl });
          } catch (e) {
            reject(e);
          }
        }
      );
    });

    console.log('Upload completed, URL:', result.publicUrl);

    return {
      s3Key: filePath,
      publicUrl: result.publicUrl,
      bucket: 'firebase-storage' // For backward compatibility
    };
  } catch (error: any) {
    console.error('Error in uploadFileToS3:', error);
    // Provide more specific error messages
    if (error.code === 'storage/unauthorized') {
      throw new Error('Permission denied. Please check Firebase Storage rules.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Storage quota exceeded. Please contact support.');
    } else if (error.code === 'storage/unauthenticated') {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error(`Upload failed: ${error.code || 'Unknown error'}`);
    }
  }
}

/**
 * Legacy function name - now uses Firebase Storage
 * @deprecated Use uploadFileToS3 instead
 */
export async function getPresignedUrl(params: { key: string; contentType: string; makePublic?: boolean }) {
  // This function is no longer needed with Firebase Storage
  // Keeping for backward compatibility but it won't be used
  throw new Error('getPresignedUrl is deprecated. Use uploadFileToS3 directly.');
}

/**
 * Legacy function - now uses Firebase Storage
 * @deprecated Use uploadFileToS3 instead
 */
export async function uploadToS3ViaPresign(file: File, folder = 'public'): Promise<string> {
  const result = await uploadFileToS3(file, { makePublic: true, keyPrefix: folder });
  return result.publicUrl;
}

